import {
  faArrowRight,
  faImage,
  faPlus,
  faRotate,
  faSpinner,
  faTrash,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Input,
  RichTextContextProvider,
  RichTextControlBar,
  RichTextInput,
  Select,
} from "@ventumsoftware/react-toolkit";
import { type FC, useMemo, useRef, useState } from "react";
import { ValidationError } from "../api/api";
import type { FlowCard } from "../api/dto/FlowCard";
import { ContentBlock, type ImageContent, type TextContent } from "../api/dto/FlowCard";
import type { CardApi } from "../components/cardFlowEditor/cardApi";
import { ModalFooter } from "../components/layout/ModalProvider";
import { useRefresh } from "../components/RefreshController";
import { languageKeysOf } from "../util/languageKeysOf";
import { resolveApiUrl } from "../util/resolveApiUrl";
import { CustomRichTextControlBar } from "../components/richTextEditor/CustomRichTextControlBar";
import { richTextExtensions } from "../components/richTextEditor/richTextExtensions";

// No language-management UI exists anywhere yet (not in scope here) — this is a fixed common-language
// superset unioned with whatever languages already appear in this flow's content, just so the two
// side-by-side Selects always have something sensible to offer, including on a brand new empty card.
const commonLanguages = ["nl", "en", "de"];

const blockTypeLabels: Record<string, string> = {
  title: "Titel",
  text: "Tekst",
  image: "Afbeelding",
};

export const EditCardContentModal: FC<{
  cards: FlowCard[];
  card: FlowCard;
  api: Pick<CardApi, "updateCard" | "uploadCardImage">;
  cardTypeOptions: Record<string, string>;
  // When provided, called with the freshly saved card instead of firing a global refresh() — lets a
  // caller (the flow editor) patch its own local state rather than trigger an app-wide resource
  // refetch for what's otherwise a small, local change.
  onSaved?: (card: FlowCard) => void;
  // When provided, used at save time instead of `props.card` to re-derive this card's other fields
  // (actions, column, positionInColumn) — `props.card` can go stale if other local state
  // changed while this modal was open (see CardFlowEditor.tsx's cardsRef comment).
  getFreshCards?: () => FlowCard[];
}> = (props) => {
  const refresh = useRefresh();
  const [name, setName] = useState<TextContent>(props.card.name);
  const [cardType, setCardType] = useState<string>(props.card.cardType);
  const [blocks, setBlocks] = useState<ContentBlock[]>(props.card.contentBlocks);
  const [errors, setErrors] = useState<string[]>([]);

  const availableLanguages = useMemo(() => {
    const found = new Set<string>(commonLanguages);
    for (const card of props.cards) {
      for (const block of card.contentBlocks) {
        for (const lang of languageKeysOf(block.content)) found.add(lang);
      }
    }
    return Array.from(found);
  }, [props.cards]);

  const [languageA, setLanguageA] = useState<string>(availableLanguages[0] ?? "nl");
  const [languageB, setLanguageB] = useState<string>(
    availableLanguages.find((l) => l !== languageA) ?? availableLanguages[0] ?? "en",
  );

  const insertBlock = (index: number, type: string) => {
    const block = new ContentBlock(crypto.randomUUID(), type, {});
    setBlocks((old) => [...old.slice(0, index), block, ...old.slice(index)]);
  };

  const removeBlock = (index: number) => {
    setBlocks((old) => old.filter((_, i) => i !== index));
  };

  const moveBlock = (index: number, direction: -1 | 1) => {
    setBlocks((old) => {
      const target = index + direction;
      if (target < 0 || target >= old.length) return old;
      const copy = [...old];
      [copy[index], copy[target]] = [copy[target], copy[index]];
      return copy;
    });
  };

  const updateName = (language: string, value: string) => {
    setName((old) => ({ ...old, [language]: value }));
  };

  const updateBlockContent = (index: number, language: string, value: string | { path: string; url: string }) => {
    setBlocks((old) =>
      old.map((block, i) => {
        if (i !== index) return block;
        // The block's own `type` already determines which of the two content shapes is correct here —
        // `value`'s runtime type always matches (title/text editors only ever call this with a string,
        // the image editor only ever with {path,url}), TS just can't express that link through the union.
        const content = { ...block.content, [language]: value } as TextContent | ImageContent;
        return new ContentBlock(block.id, block.type, content);
      }),
    );
  };

  const save = async () => {
    const baseCard = props.getFreshCards?.().find((c) => c.uuid === props.card.uuid) ?? props.card;
    const updated = await props.api.updateCard({ ...baseCard, name, cardType, contentBlocks: blocks }).catch((e) => {
      if (e instanceof ValidationError) {
        setErrors(Object.values(e.errors).flat());
      }
      throw e;
    });
    if (props.onSaved) {
      props.onSaved(updated);
    } else {
      refresh();
    }
  };

  return (
    <div className={"flex flex-col space-y-4"}>
      <div>
        <Select
          label={"Type"}
          options={props.cardTypeOptions}
          value={cardType}
          onChange={setCardType}
        />
      </div>

      {/* Fixed card title — the card's own name, not a content block: always first, can't be
          reordered or removed like the blocks below it can. Uses the same language selection as
          the block editor below it. */}
      <div className={"border border-slate-300 bg-slate-50 rounded-lg overflow-hidden"}>
        <div className={"flex items-center justify-end px-3 py-1 bg-slate-100/60"}>
          <CopyToBButton
            disabled={!name[languageA]}
            languageB={languageB}
            onCopy={() => updateName(languageB, name[languageA] ?? "")}
          />
        </div>
        <div className={"grid grid-cols-2"}>
          <div className={"p-3 border-r border-slate-300"}>
            <Input
              type={"text"}
              label={`Titel (kaartnaam) — ${languageA.toUpperCase()}`}
              value={name[languageA] ?? ""}
              onChange={(value) => updateName(languageA, value)}
            />
          </div>
          <div className={"p-3"}>
            <Input
              type={"text"}
              label={`Titel (kaartnaam) — ${languageB.toUpperCase()}`}
              value={name[languageB] ?? ""}
              onChange={(value) => updateName(languageB, value)}
            />
          </div>
        </div>
      </div>

      <div className={"border border-slate-200 rounded-lg overflow-hidden"}>
        <div className={"grid grid-cols-2 bg-slate-50 border-b border-slate-200"}>
          <div className={"p-2 border-r border-slate-200"}>
            <Select
              label={"Taal"}
              inline
              options={optionsFor(availableLanguages)}
              value={languageA}
              onChange={setLanguageA}
            />
          </div>
          <div className={"p-2"}>
            <Select
              label={"Taal"}
              inline
              options={optionsFor(availableLanguages)}
              value={languageB}
              onChange={setLanguageB}
            />
          </div>
        </div>

        <InsertBlockRow onInsert={(type) => insertBlock(0, type)} />
        {blocks.map((block, index) => (
          <div key={block.id} className={"border-t border-slate-200"}>
            <ContentBlockRow
              block={block}
              index={index}
              canMoveUp={index > 0}
              canMoveDown={index < blocks.length - 1}
              languageA={languageA}
              languageB={languageB}
              onMove={moveBlock}
              onRemove={removeBlock}
              onChangeContent={updateBlockContent}
              uploadCardImage={props.api.uploadCardImage}
            />
            <InsertBlockRow onInsert={(type) => insertBlock(index + 1, type)} />
          </div>
        ))}
      </div>

      {errors.length > 0 && (
        <div className={"text-red-600 text-sm"}>
          {errors.map((error) => (
            <div key={error}>{error}</div>
          ))}
        </div>
      )}
      <ModalFooter text={"Opslaan"} onSubmit={save} disabled={!Object.values(name).some((v) => v)} />
    </div>
  );
};

function optionsFor(languages: string[]): Record<string, string> {
  return Object.fromEntries(languages.map((l) => [l, l.toUpperCase()]));
}

// Copies taal A's content over taal B's — most useful for images (reuses the same uploaded {path,url},
// no re-upload needed) but works for title/text too, as a starting point for further edits in B.
const CopyToBButton: FC<{ disabled: boolean; languageB: string; onCopy: () => void }> = (props) => {
  return (
    <button
      type={"button"}
      disabled={props.disabled}
      onClick={props.onCopy}
      title={`Kopieer naar ${props.languageB.toUpperCase()}`}
      className={
        "text-xs flex items-center space-x-1 text-slate-500 hover:bg-slate-100 rounded px-1 disabled:opacity-25"
      }
    >
      <FontAwesomeIcon icon={faArrowRight} />
      <span>{props.languageB.toUpperCase()}</span>
    </button>
  );
};

const InsertBlockRow: FC<{ onInsert: (type: string) => void }> = (props) => {
  return (
    <div className={"flex items-center justify-start space-x-2 px-2 py-1"}>
      {Object.entries(blockTypeLabels).map(([type, label]) => (
        <button
          key={type}
          type={"button"}
          onClick={() => props.onInsert(type)}
          className={"text-xs flex items-center space-x-1 text-slate-500 hover:bg-slate-100 rounded px-2 py-0.5"}
        >
          <FontAwesomeIcon icon={faPlus} />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
};

const ContentBlockRow: FC<{
  block: ContentBlock;
  index: number;
  canMoveUp: boolean;
  canMoveDown: boolean;
  languageA: string;
  languageB: string;
  onMove: (index: number, direction: -1 | 1) => void;
  onRemove: (index: number) => void;
  onChangeContent: (index: number, language: string, value: string | { path: string; url: string }) => void;
  uploadCardImage: CardApi["uploadCardImage"];
}> = (props) => {
  return (
    <div>
      <div className={"flex items-center justify-between px-3 py-1 bg-slate-50/60"}>
        <span className={"text-xs font-medium uppercase text-slate-500"}>
          {blockTypeLabels[props.block.type] ?? props.block.type}
        </span>
        <div className={"flex items-center space-x-1"}>
          <button
            type={"button"}
            disabled={!props.canMoveUp}
            onClick={() => props.onMove(props.index, -1)}
            className={"text-xs text-slate-500 hover:bg-slate-100 rounded px-1 disabled:opacity-25"}
          >
            ▲
          </button>
          <button
            type={"button"}
            disabled={!props.canMoveDown}
            onClick={() => props.onMove(props.index, 1)}
            className={"text-xs text-slate-500 hover:bg-slate-100 rounded px-1 disabled:opacity-25"}
          >
            ▼
          </button>
          <CopyToBButton
            disabled={!props.block.content[props.languageA]}
            languageB={props.languageB}
            onCopy={() => props.onChangeContent(props.index, props.languageB, props.block.content[props.languageA])}
          />
          <button
            type={"button"}
            onClick={() => props.onRemove(props.index)}
            className={"text-xs text-red-600 hover:bg-red-50 rounded px-1"}
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </div>
      </div>
      <div className={"grid grid-cols-2"}>
        <div className={"p-3 border-r border-slate-200"}>
          <ContentBlockLanguageEditor
            block={props.block}
            language={props.languageA}
            onChange={(value) => props.onChangeContent(props.index, props.languageA, value)}
            uploadCardImage={props.uploadCardImage}
          />
        </div>
        <div className={"p-3"}>
          <ContentBlockLanguageEditor
            block={props.block}
            language={props.languageB}
            onChange={(value) => props.onChangeContent(props.index, props.languageB, value)}
            uploadCardImage={props.uploadCardImage}
          />
        </div>
      </div>
    </div>
  );
};

const ContentBlockLanguageEditor: FC<{
  block: ContentBlock;
  language: string;
  onChange: (value: string | { path: string; url: string }) => void;
  uploadCardImage: CardApi["uploadCardImage"];
}> = (props) => {
  if (props.block.type === "title") {
    const content = props.block.content as TextContent;
    return (
      <input
        type={"text"}
        value={content[props.language] ?? ""}
        onChange={(e) => props.onChange(e.target.value)}
        placeholder={`Titel (${props.language.toUpperCase()})`}
        className={
          "border-2 border-slate-200 outline-brand-700 text-black rounded-sm text-base font-normal px-2 py-1 min-h-10 w-full"
        }
      />
    );
  }
  if (props.block.type === "text") {
    const content = props.block.content as TextContent;
    return (
      <RichTextContextProvider
        content={content[props.language] ?? ""}
        setContent={props.onChange}
        extensions={richTextExtensions}
      >
        <CustomRichTextControlBar />
        <RichTextInput className={"border border-slate-200 rounded min-h-24 p-2"} />
      </RichTextContextProvider>
    );
  }
  if (props.block.type === "image") {
    const content = props.block.content as ImageContent;
    return (
      <ImageBlockLanguageEditor
        current={content[props.language]}
        onChange={props.onChange}
        uploadCardImage={props.uploadCardImage}
      />
    );
  }
  return <div className={"text-xs text-slate-400"}>Onbekend bloktype "{props.block.type}"</div>;
};

// Mirrors UploadChecklistCardImageRequest (gateway-api): jpeg/png/gif/svg, max 2048 KB.
const acceptedImageTypes = ["image/jpeg", "image/png", "image/gif", "image/svg+xml"];
const maxImageBytes = 2048 * 1024;

const ImageBlockLanguageEditor: FC<{
  current?: { path: string; url: string };
  onChange: (value: { path: string; url: string }) => void;
  uploadCardImage: CardApi["uploadCardImage"];
}> = (props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Uploads right away on selection/drop — no separate "Uploaden" step. The local preview shows
  // immediately (with a spinner) and is dropped again once the uploaded image replaces it.
  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    if (!acceptedImageTypes.includes(file.type)) {
      setError("Alleen JPG, PNG, GIF of SVG afbeeldingen zijn toegestaan");
      return;
    }
    if (file.size > maxImageBytes) {
      setError("De afbeelding is te groot (maximaal 2 MB)");
      return;
    }
    setError(null);
    setUploading(true);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    try {
      props.onChange(await props.uploadCardImage(file));
    } catch (e) {
      if (e instanceof ValidationError) {
        setError(Object.values(e.errors).flat().join(", "));
      } else {
        setError("Upload mislukt, probeer het opnieuw");
      }
    } finally {
      setUploading(false);
      setPreviewUrl(null);
      URL.revokeObjectURL(objectUrl);
    }
  };

  const openPicker = () => inputRef.current?.click();
  const displayUrl = previewUrl ?? (props.current?.url ? resolveApiUrl(props.current.url) : null);

  return (
    <div className={"flex flex-col space-y-2"}>
      {displayUrl ? (
        <img src={displayUrl} alt={""} className={"max-h-32 object-contain border border-slate-100 rounded"} />
      ) : (
        <div
          className={
            "flex items-center space-x-2 text-xs text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1.5"
          }
        >
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>Nog geen afbeelding geüpload voor deze taal</span>
        </div>
      )}
      <input
        ref={inputRef}
        type={"file"}
        accept={acceptedImageTypes.join(",")}
        className={"hidden"}
        onChange={(e) => {
          handleFile(e.target.files?.[0]);
          // Allows re-selecting the same file after a failed upload.
          e.target.value = "";
        }}
      />
      {displayUrl ? (
        <div className={"flex flex-col items-start space-y-2"}>
          <div className={"relative border border-slate-200 rounded bg-slate-50 p-1"}>
            <img src={displayUrl} alt={""} className={"max-h-40 object-contain rounded"} />
            {uploading && (
              <div className={"absolute inset-0 flex items-center justify-center bg-white/70 rounded text-slate-600"}>
                <FontAwesomeIcon icon={faSpinner} spin />
              </div>
            )}
          </div>
          <button
            type={"button"}
            disabled={uploading}
            onClick={openPicker}
            className={
              "text-xs flex items-center space-x-1 border border-slate-200 rounded px-2 py-1 hover:bg-slate-100 disabled:opacity-50"
            }
          >
            <FontAwesomeIcon icon={faRotate} />
            <span>{uploading ? "Bezig met uploaden..." : "Afbeelding vervangen"}</span>
          </button>
        </div>
      ) : (
        <button
          type={"button"}
          disabled={uploading}
          onClick={openPicker}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            handleFile(e.dataTransfer.files?.[0]);
          }}
          className={`flex flex-col items-center justify-center space-y-1 w-full min-h-28 px-3 py-4 rounded border-2 border-dashed text-center transition-colors ${
            dragging
              ? "border-brand-700 bg-brand-50"
              : "border-slate-300 bg-slate-50 hover:bg-slate-100 hover:border-slate-400"
          }`}
        >
          <FontAwesomeIcon icon={faImage} className={"text-2xl text-slate-400"} />
          <span className={"text-sm font-medium text-slate-700"}>
            Sleep een afbeelding hierheen of klik om te kiezen
          </span>
          <span className={"text-xs text-slate-500"}>JPG, PNG, GIF of SVG, maximaal 2 MB</span>
        </button>
      )}
      {error && (
        <div className={"flex items-center space-x-1 text-xs text-red-600"}>
          <FontAwesomeIcon icon={faTriangleExclamation} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
};
